import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '../../../../components/Breadcrumb';
import Page from '../../../../components/Page';
import Pagination from '../../../../components/Pagination';
import client from '../../../../lib/feathers';

// ─── Badge style maps ────────────────────────────────────────────────────────

const journeyStatusBadgeStyles = {
  active: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  archived: 'bg-gray-100 text-gray-800',
  completed: 'bg-blue-100 text-blue-800',
};

const enrollmentStatusBadgeStyles = {
  active: 'bg-green-100 text-green-800',
  completed: 'bg-blue-100 text-blue-800',
  exited: 'bg-red-100 text-red-800',
  paused: 'bg-yellow-100 text-yellow-800',
};

const audienceBadgeStyles = {
  outreach: 'bg-blue-100 text-blue-800',
  business: 'bg-purple-100 text-purple-800',
  personal: 'bg-emerald-100 text-emerald-800',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ENROLLMENT_STATUS_LIST = ['all', 'active', 'completed', 'exited', 'paused'];
const ENROLLMENTS_LIMIT = 25;

const fmtRate = (numerator, denominator) => {
  if (!denominator || denominator === 0) return null;
  return `${(((numerator || 0) / denominator) * 100).toFixed(1)}%`;
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value }) {
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
        <dd className="mt-1 text-3xl font-semibold text-gray-900">{value}</dd>
      </div>
    </div>
  );
}

function StepTimeline({ steps, stepStats }) {
  const [expandedSteps, setExpandedSteps] = useState({});

  const toggleBody = (stepId) => {
    setExpandedSteps((prev) => ({ ...prev, [stepId]: !prev[stepId] }));
  };

  // Compute cumulative delay day for each step
  let cumulativeDay = 0;
  const stepsWithDay = (steps || []).map((step) => {
    const day = cumulativeDay;
    cumulativeDay += step.delayDays || 0;
    return { ...step, cumulativeDay: day };
  });

  if (!stepsWithDay.length) {
    return (
      <div className="py-12 text-center text-sm text-gray-500">
        No steps configured for this journey.
      </div>
    );
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {stepsWithDay.map((step, idx) => {
          const isLast = idx === stepsWithDay.length - 1;
          const stats = stepStats && stepStats[step.id];
          const sent = stats ? (stats.sent || 0) : null;
          const openRate = stats ? fmtRate(stats.opened, stats.sent) : null;
          const clickRate = stats ? fmtRate(stats.clicked, stats.sent) : null;
          const isExpanded = !!expandedSteps[step.id];

          return (
            <li key={step.id || idx}>
              <div className="relative pb-8">
                {/* Connector line */}
                {!isLast && (
                  <span
                    className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  />
                )}

                <div className="relative flex items-start space-x-4">
                  {/* Step index circle */}
                  <div className="flex-shrink-0">
                    <span className="h-10 w-10 rounded-full bg-primary flex items-center justify-center ring-4 ring-white">
                      <span className="text-xs font-bold text-white">{idx + 1}</span>
                    </span>
                  </div>

                  {/* Step card */}
                  <div className="min-w-0 flex-1 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                    {/* Card header */}
                    <div className="px-4 py-3 border-l-4 border-primary">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-gray-900">
                              {step.name || `Step ${idx + 1}`}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                              Day {step.cumulativeDay}
                            </span>
                            {(step.delayDays > 0) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700">
                                +{step.delayDays}d delay
                              </span>
                            )}
                          </div>
                          {step.subject && (
                            <p className="mt-0.5 text-sm text-gray-600 truncate">
                              Subject: <span className="text-gray-800">{step.subject}</span>
                            </p>
                          )}
                        </div>

                        {/* Per-step stats */}
                        {stats && (
                          <div className="flex-shrink-0 flex items-center gap-4 text-xs text-gray-500">
                            <span>
                              <span className="font-medium text-gray-800">{(sent || 0).toLocaleString()}</span>
                              {' '}sent
                            </span>
                            {openRate && (
                              <span>
                                <span className="font-medium text-gray-800">{openRate}</span>
                                {' '}opens
                              </span>
                            )}
                            {clickRate && (
                              <span>
                                <span className="font-medium text-gray-800">{clickRate}</span>
                                {' '}clicks
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Body preview toggle */}
                    {step.body && (
                      <div className="border-t border-gray-100">
                        <button
                          type="button"
                          onClick={() => toggleBody(step.id || idx)}
                          className="w-full px-4 py-2 text-left text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 flex items-center justify-between"
                        >
                          <span>Body preview</span>
                          <svg
                            className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {isExpanded && (
                          <div className="px-4 pb-4">
                            <div
                              className="prose prose-sm max-w-none text-gray-700 border border-gray-200 rounded p-3 bg-gray-50 overflow-auto max-h-64"
                              dangerouslySetInnerHTML={{ __html: step.body }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── Enrollment detail panel ──────────────────────────────────────────────────

function EnrollmentPanel({ enrollment, onClose }) {
  if (!enrollment) return null;

  const recipient = enrollment.contact || enrollment.user;
  const recipientName = recipient
    ? (`${recipient.firstName || recipient.name || ''} ${recipient.lastName || ''}`.trim() || recipient.email)
    : null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm mt-3">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900">Enrollment Detail</h4>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <dl className="divide-y divide-gray-100">
        {recipientName && (
          <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Recipient</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              {recipientName}
              {recipient?.email && recipientName !== recipient.email && (
                <div className="text-xs text-gray-400">{recipient.email}</div>
              )}
            </dd>
          </div>
        )}
        {enrollment.listing && (
          <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Listing</dt>
            <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
              <Link
                to={`/admin/listings/${enrollment.listing.id}`}
                className="text-primary hover:underline"
              >
                {enrollment.listing.name}
              </Link>
            </dd>
          </div>
        )}
        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
          <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</dt>
          <dd className="mt-1 sm:mt-0 sm:col-span-2">
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${enrollmentStatusBadgeStyles[enrollment.status] || 'bg-gray-100 text-gray-800'}`}>
              {enrollment.status}
            </span>
          </dd>
        </div>
        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
          <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Current Step</dt>
          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
            {enrollment.currentStepName || enrollment.currentStep || <span className="text-gray-400">—</span>}
          </dd>
        </div>
        <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
          <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Enrolled</dt>
          <dd className="mt-1 text-sm text-gray-500 sm:mt-0 sm:col-span-2">
            {enrollment.enrolledAt
              ? format(new Date(enrollment.enrolledAt), 'MMM d, yyyy h:mm a')
              : format(new Date(enrollment.createdAt), 'MMM d, yyyy h:mm a')}
          </dd>
        </div>
        {enrollment.nextSendAt && (
          <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Next Send</dt>
            <dd className="mt-1 text-sm text-gray-500 sm:mt-0 sm:col-span-2">
              {format(new Date(enrollment.nextSendAt), 'MMM d, yyyy h:mm a')}
            </dd>
          </div>
        )}
        {enrollment.completedAt && (
          <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Completed</dt>
            <dd className="mt-1 text-sm text-gray-500 sm:mt-0 sm:col-span-2">
              {format(new Date(enrollment.completedAt), 'MMM d, yyyy h:mm a')}
            </dd>
          </div>
        )}
        {enrollment.exitReason && (
          <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Exit Reason</dt>
            <dd className="mt-1 text-sm text-gray-700 sm:mt-0 sm:col-span-2">{enrollment.exitReason}</dd>
          </div>
        )}
        {enrollment.stepsSent != null && (
          <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Steps Sent</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{enrollment.stepsSent}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function JourneyDetail() {
  const { jid } = useParams();

  // Core data
  const [journey, setJourney] = useState(null);
  const [stepStats, setStepStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tab
  const [activeTab, setActiveTab] = useState('steps');

  // Pause/resume
  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState(null);

  // Enrollments tab
  const [enrollments, setEnrollments] = useState([]);
  const [enrollmentsTotal, setEnrollmentsTotal] = useState(0);
  const [enrollmentsPage, setEnrollmentsPage] = useState(0);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);
  const [enrollmentStatusFilter, setEnrollmentStatusFilter] = useState('all');
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadJourney = async () => {
    setLoading(true);
    setError(null);
    try {
      const [journeyResult, statsResult] = await Promise.all([
        client.service('admin/outreach-journey').get(jid),
        client.service('admin/outreach-journey').get(jid, { query: { $action: 'stats' } }),
      ]);
      setJourney(journeyResult);
      // stepStats may be a map of stepId -> { sent, opened, clicked }
      setStepStats(statsResult.steps || statsResult || null);
    } catch (e) {
      console.error('Error loading journey:', e);
      setError('Failed to load journey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadEnrollments = async (page = 0, statusFilter = 'all') => {
    setEnrollmentsLoading(true);
    try {
      const q = {
        $action: 'enrollments',
        $limit: ENROLLMENTS_LIMIT,
        $skip: page * ENROLLMENTS_LIMIT,
      };
      if (statusFilter && statusFilter !== 'all') {
        q.status = statusFilter;
      }
      const result = await client.service('admin/outreach-journey').get(jid, { query: q });
      // API may return { data, total } or an array
      if (result && result.data) {
        setEnrollments(result.data);
        setEnrollmentsTotal(result.total || result.data.length);
      } else if (Array.isArray(result)) {
        setEnrollments(result);
        setEnrollmentsTotal(result.length);
      } else {
        setEnrollments([]);
        setEnrollmentsTotal(0);
      }
    } catch (e) {
      console.error('Error loading enrollments:', e);
    } finally {
      setEnrollmentsLoading(false);
    }
  };

  // ── Pause / Resume ──────────────────────────────────────────────────────────

  const handleToggleStatus = async () => {
    if (!journey || journey.status === 'archived') return;
    const nextStatus = journey.status === 'active' ? 'paused' : 'active';
    const verb = nextStatus === 'paused' ? 'pause' : 'resume';
    if (!window.confirm(`Are you sure you want to ${verb} this journey?`)) return;

    setToggling(true);
    setToggleError(null);
    try {
      const updated = await client.service('admin/outreach-journey').patch(jid, { status: nextStatus });
      setJourney(updated);
    } catch (e) {
      console.error('Error toggling journey status:', e);
      setToggleError(e.message || `Failed to ${verb} journey. Please try again.`);
    } finally {
      setToggling(false);
    }
  };

  // ── Tab / filter handlers ───────────────────────────────────────────────────

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'enrollments' && enrollments.length === 0) {
      loadEnrollments(0, enrollmentStatusFilter);
    }
  };

  const handleEnrollmentStatusFilter = (status) => {
    setEnrollmentStatusFilter(status);
    setEnrollmentsPage(0);
    setSelectedEnrollment(null);
    loadEnrollments(0, status);
  };

  const navigateEnrollmentsPage = (p = 0) => {
    if (p * ENROLLMENTS_LIMIT > enrollmentsTotal || p * ENROLLMENTS_LIMIT < 0) return;
    setEnrollmentsPage(p);
    setSelectedEnrollment(null);
    loadEnrollments(p, enrollmentStatusFilter);
  };

  const handleEnrollmentRowClick = (enrollment) => {
    setSelectedEnrollment((prev) => (prev && prev.id === enrollment.id ? null : enrollment));
  };

  // ── Effects ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    loadJourney();
  }, [jid]);

  // ── Loading / error states ──────────────────────────────────────────────────

  if (loading) {
    return (
      <Page title="Journey">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Page>
    );
  }

  if (error || !journey) {
    return (
      <Page title="Journey">
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-red-800">{error || 'Journey not found.'}</p>
        </div>
      </Page>
    );
  }

  // ── Derived values ──────────────────────────────────────────────────────────

  const totalEnrolled = journey.enrollmentCount || 0;
  const activeCount = journey.activeEnrollmentCount || 0;
  const completedCount = journey.completedEnrollmentCount != null
    ? journey.completedEnrollmentCount
    : Math.max(0, totalEnrolled - activeCount);
  const exitedCount = journey.exitedEnrollmentCount || 0;

  const canToggle = journey.status !== 'archived';
  const isActive = journey.status === 'active';

  const tabs = [
    { key: 'steps', label: `Steps${journey.steps ? ` (${journey.steps.length})` : ''}` },
    { key: 'enrollments', label: 'Enrollments' },
  ];

  return (
    <Page title={journey.name}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="pb-5 border-b border-gray-200">
        <div className="sm:flex sm:items-start sm:justify-between">
          <Breadcrumb
            elements={[
              { name: 'Outreach', href: '/admin/outreach' },
              { name: 'Journeys', href: '/admin/outreach/journeys' },
              { name: journey.name },
            ]}
          />
          <div className="mt-3 sm:mt-0 sm:ml-4 flex items-center gap-2 flex-shrink-0">
            <Link
              to={`/admin/outreach/journeys/new?id=${jid}`}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Edit
            </Link>
            {canToggle && (
              <button
                type="button"
                onClick={handleToggleStatus}
                disabled={toggling}
                className={`inline-flex items-center px-3 py-2 border rounded-md shadow-sm text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                  ${isActive
                    ? 'border-yellow-300 text-yellow-700 bg-yellow-50 hover:bg-yellow-100'
                    : 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100'}`}
              >
                {toggling ? 'Updating...' : isActive ? 'Pause Journey' : 'Resume Journey'}
              </button>
            )}
          </div>
        </div>

        {toggleError && (
          <div className="mt-3 rounded-md bg-red-50 p-3">
            <p className="text-sm text-red-700">{toggleError}</p>
          </div>
        )}
      </div>

      {/* ── Journey meta card ───────────────────────────────────────────────── */}
      <div className="mt-6 bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg leading-6 font-semibold text-gray-900 truncate">{journey.name}</h2>
            {journey.description && (
              <p className="mt-1 text-sm text-gray-500">{journey.description}</p>
            )}
          </div>
          <div className="flex-shrink-0 flex items-center gap-2">
            <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full capitalize ${audienceBadgeStyles[journey.audience] || 'bg-gray-100 text-gray-800'}`}>
              {capitalize(journey.audience)}
            </span>
            <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full capitalize ${journeyStatusBadgeStyles[journey.status] || 'bg-gray-100 text-gray-800'}`}>
              {journey.status}
            </span>
          </div>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            {journey.fromEmail && (
              <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">From Email</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{journey.fromEmail}</dd>
              </div>
            )}
            {journey.masterTemplateId && (
              <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Master Template ID</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 font-mono">{journey.masterTemplateId}</dd>
              </div>
            )}
            {journey.trigger && (
              <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Trigger</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 capitalize">
                  {typeof journey.trigger === 'object'
                    ? (journey.trigger.event || JSON.stringify(journey.trigger))
                    : journey.trigger}
                </dd>
              </div>
            )}
            <div className="bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {format(new Date(journey.createdAt), 'MMM d, yyyy h:mm a')}
              </dd>
            </div>
            {journey.updatedAt && journey.updatedAt !== journey.createdAt && (
              <div className="bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {format(new Date(journey.updatedAt), 'MMM d, yyyy h:mm a')}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* ── Stats cards ─────────────────────────────────────────────────────── */}
      <div className="mt-6">
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Total Enrolled" value={totalEnrolled.toLocaleString()} />
          <StatCard label="Active" value={activeCount.toLocaleString()} />
          <StatCard label="Completed" value={completedCount.toLocaleString()} />
          <StatCard label="Exited" value={exitedCount.toLocaleString()} />
        </dl>
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────────────── */}
      <div className="mt-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabChange(tab.key)}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-gray-500 text-gray-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* ── Steps tab ─────────────────────────────────────────────────────── */}
        {activeTab === 'steps' && (
          <div className="mt-6">
            {(!journey.steps || journey.steps.length === 0) ? (
              <div className="bg-white shadow sm:rounded-lg px-6 py-12 text-center">
                <p className="text-sm text-gray-500">No steps configured for this journey.</p>
                <Link
                  to={`/admin/outreach/journeys/new?id=${jid}`}
                  className="mt-3 inline-flex items-center text-sm text-primary hover:text-red-800 font-medium"
                >
                  Edit journey to add steps
                </Link>
              </div>
            ) : (
              <StepTimeline steps={journey.steps} stepStats={stepStats} />
            )}
          </div>
        )}

        {/* ── Enrollments tab ───────────────────────────────────────────────── */}
        {activeTab === 'enrollments' && (
          <div className="mt-6">

            {/* Status filter */}
            <div className="mb-4 border-b border-gray-200">
              <nav className="-mb-px flex space-x-6" aria-label="Enrollment status filter">
                {ENROLLMENT_STATUS_LIST.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleEnrollmentStatusFilter(s)}
                    className={`capitalize whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                      enrollmentStatusFilter === s
                        ? 'border-gray-500 text-gray-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {s === 'all' ? 'All' : capitalize(s)}
                  </button>
                ))}
              </nav>
            </div>

            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">
                {enrollmentsTotal.toLocaleString()} enrollment{enrollmentsTotal !== 1 ? 's' : ''}
              </h3>
              {enrollmentsTotal > ENROLLMENTS_LIMIT && (
                <Pagination
                  total={enrollmentsTotal}
                  limit={ENROLLMENTS_LIMIT}
                  page={enrollmentsPage}
                  navigateToPage={navigateEnrollmentsPage}
                />
              )}
            </div>

            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recipient
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Listing
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current Step
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Enrolled
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Next Send
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {enrollmentsLoading && (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                        Loading enrollments...
                      </td>
                    </tr>
                  )}
                  {!enrollmentsLoading && enrollments.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                        No enrollments found{enrollmentStatusFilter !== 'all' ? ` with status "${enrollmentStatusFilter}"` : ''}.
                      </td>
                    </tr>
                  )}
                  {!enrollmentsLoading && enrollments.map((enrollment) => {
                    const recipient = enrollment.contact || enrollment.user;
                    const recipientName = recipient
                      ? (`${recipient.firstName || recipient.name || ''} ${recipient.lastName || ''}`.trim() || null)
                      : null;
                    const recipientEmail = recipient?.email;
                    const isSelected = selectedEnrollment && selectedEnrollment.id === enrollment.id;

                    return (
                      <React.Fragment key={enrollment.id}>
                        <tr
                          className={`cursor-pointer transition-colors ${isSelected ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                          onClick={() => handleEnrollmentRowClick(enrollment)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            {recipient ? (
                              <div>
                                {recipientName && (
                                  <div className="text-sm font-medium text-gray-900">{recipientName}</div>
                                )}
                                {recipientEmail && (
                                  <div className={`text-sm ${recipientName ? 'text-gray-500' : 'font-medium text-gray-900'}`}>
                                    {recipientEmail}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400 italic">Unknown</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {enrollment.listing ? (
                              <Link
                                to={`/admin/listings/${enrollment.listing.id}`}
                                className="text-sm text-gray-900 hover:text-primary hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {enrollment.listing.name}
                              </Link>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {enrollment.currentStepName || enrollment.currentStep || (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${enrollmentStatusBadgeStyles[enrollment.status] || 'bg-gray-100 text-gray-800'}`}>
                              {enrollment.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {enrollment.enrolledAt || enrollment.createdAt
                              ? format(new Date(enrollment.enrolledAt || enrollment.createdAt), 'MMM d, yyyy')
                              : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {enrollment.nextSendAt
                              ? format(new Date(enrollment.nextSendAt), 'MMM d, yyyy')
                              : <span className="text-gray-400">—</span>}
                          </td>
                        </tr>
                        {isSelected && (
                          <tr>
                            <td colSpan={6} className="px-6 pb-4">
                              <EnrollmentPanel
                                enrollment={enrollment}
                                onClose={() => setSelectedEnrollment(null)}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              {enrollmentsTotal > ENROLLMENTS_LIMIT && (
                <Pagination
                  total={enrollmentsTotal}
                  limit={ENROLLMENTS_LIMIT}
                  page={enrollmentsPage}
                  navigateToPage={navigateEnrollmentsPage}
                />
              )}
            </div>
          </div>
        )}
      </div>

    </Page>
  );
}
